import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { client } from '../client'; // Removed urlFor as it's not used for news images here

// Helper function to format date (optional, can also format directly in JSX)
const formatDate = (dateString) => {
  if (!dateString) return '';
  
  // If dateString is YYYY-MM-DD, replace hyphens with slashes to parse as local date.
  // Otherwise (e.g., if it includes time or is already a Date object),
  // new Date() should handle it appropriately.
  let processedDateString = dateString;
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    processedDateString = dateString.replace(/-/g, '/');
  }
  
  const date = new Date(processedDateString);
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

const HomePage = () => {
  // State for Sanity data, loading, and error
  const [homePageData, setHomePageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Placeholder data for news - keeping exact same content - REMOVED as it's unused
  // const recentNews = [
  //   {
  //     id: 1,
  //     title: 'Summer School Registration Now Open',
  //     date: 'May 15, 2023',
  //     excerpt: 'Register your child for our summer enrichment program. Spaces are limited.',
  //     image: '/images/homepage/PS52-news1.jpg'
  //   },
  //   {
  //     id: 2,
  //     title: 'School Closed for Memorial Day',
  //     date: 'May 10, 2023',
  //     excerpt: 'School will be closed on Monday, May 29th in observance of Memorial Day.',
  //     image: '/images/homepage/PS52-news2.jpg'
  //   },
  //   {
  //     id: 3,
  //     title: 'Spring Concert a Success',
  //     date: 'May 5, 2023',
  //     excerpt: 'Thank you to all who attended our Spring Concert. View photos here.',
  //     image: '/images/homepage/PS52-news3.jpg'
  //   }
  // ];

  // State for dynamic upcoming events
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  
  // State for program carousel
  const [programIndex, setProgramIndex] = useState(0);

  // State for news carousel
  const [newsIndex, setNewsIndex] = useState(0);

  // Fetch HomePage data from Sanity
  useEffect(() => {
    // Update the query to dereference newsItems and select needed fields
    const query = `*[_type == "homePage"][0]{
      ..., // Fetch all top-level fields from homePage
      welcomeImage{asset->{url}},
      videoFile{asset->{url}},
      // Dereference newsItems and select specific fields from referenced newsArticle
      newsItems[]->{
        _id, // Need the ID for keys if _key isn't automatically included
        title,
        slug,
        publicationDate,
        excerpt,
        mainImage{asset->{url}} // Fetch the main image URL
      },
      programItems[]->{
        _key,
        title,
        "slug": slug.current,
        image{asset->{url}}
      },
      statisticsItems[]{
        _key,
        label,
        value
      }
    }`;

    client.fetch(query)
      .then((data) => {
        setHomePageData(data);
        setLoading(false);
        console.log('Fetched HomePage Data:', data); // Debug line
        console.log('Fetched News Items:', data?.newsItems); // Debug line for news items
      })
      .catch((err) => {
        console.error('Error fetching Sanity data:', err);
        setError('Failed to load page content.');
        setLoading(false);
      });
  }, []);

  // Fetch upcoming events from calendar data
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    
    // School events from CalendarPage - same structure but only needed fields
    const schoolEvents = [
      // April Events
      {
        id: '1',
        title: 'PTA Meeting / Student of the Month',
        start: `${currentYear}-04-08T08:15:00`,
        location: 'School Auditorium'
      },
      {
        id: '2',
        title: 'Harlem Wizards Assembly',
        start: `${currentYear}-04-09`,
        location: 'School Gymnasium'
      },
      {
        id: '3',
        title: 'Spring Recess - Schools Closed',
        start: `${currentYear}-04-14`,
        location: ''
      },
      {
        id: '4',
        title: 'STEM Night (Grades K-2)',
        start: `${currentYear}-04-22T16:00:00`,
        location: 'School Gymnasium and Classrooms'
      },
      {
        id: '5',
        title: 'STEM Night (Grades 3-5)',
        start: `${currentYear}-04-22T17:30:00`,
        location: 'School Gymnasium and Classrooms'
      },
      
      // May Events
      {
        id: '6',
        title: 'Evening Parent-Teacher Conferences',
        start: `${currentYear}-05-01T17:00:00`,
        location: 'Classrooms'
      },
      {
        id: '7',
        title: '5th Grade "25" Arial Picture',
        start: `${currentYear}-05-01T10:00:00`,
        location: 'School Field'
      },
      {
        id: '8',
        title: 'PTA Plant Sale',
        start: `${currentYear}-05-09`,
        location: 'School Entrance'
      },
      {
        id: '9',
        title: 'Field Day (Grades 3-5)',
        start: `${currentYear}-05-12`,
        location: 'School Field'
      },
      {
        id: '10',
        title: 'PTA Meeting / Student of the Month / Elections',
        start: `${currentYear}-05-13T19:00:00`,
        location: 'School Auditorium'
      },
      {
        id: '11',
        title: 'Field Day (Grades K-2)',
        start: `${currentYear}-05-16`,
        location: 'School Field'
      },
      {
        id: '12',
        title: 'Spring Concert (Grade 4)',
        start: `${currentYear}-05-20T18:00:00`,
        location: 'School Auditorium'
      },
      {
        id: '13',
        title: 'Field Day (PreK)',
        start: `${currentYear}-05-21`,
        location: 'School Field'
      },
      {
        id: '14',
        title: 'Memorial Day - Schools Closed',
        start: `${currentYear}-05-26`,
        location: ''
      },
      {
        id: '15',
        title: 'Spring Concert (Grade 5)',
        start: `${currentYear}-05-27T18:00:00`,
        location: 'School Auditorium'
      },
      {
        id: '16',
        title: '5th Grade Chelsea Piers Trip',
        start: `${currentYear}-05-29`,
        location: 'Chelsea Piers'
      },
      {
        id: '17',
        title: 'PTA School Carnival',
        start: `${currentYear}-05-30`,
        location: 'School Grounds'
      },
      
      // June Events
      {
        id: '18',
        title: 'Eid al-Adha / Anniversary Day - Schools Closed',
        start: `${currentYear}-06-05`,
        location: ''
      },
      {
        id: '19',
        title: 'Clerical Day - No Classes for Students',
        start: `${currentYear}-06-06`,
        location: ''
      },
      {
        id: '20',
        title: 'Kindergarten Magic Show',
        start: `${currentYear}-06-12`,
        location: 'School Auditorium'
      },
      {
        id: '21',
        title: 'Last Day of NIA',
        start: `${currentYear}-06-13`,
        location: ''
      },
      {
        id: '22',
        title: 'PreK and Kindergarten Stepping Up Ceremonies',
        start: `${currentYear}-06-16`,
        location: 'School Auditorium'
      },
      {
        id: '23',
        title: 'Juneteenth - Schools Closed',
        start: `${currentYear}-06-19`,
        location: ''
      },
      {
        id: '24',
        title: '5th Grade Graduation',
        start: `${currentYear}-06-20`,
        location: 'School Auditorium'
      },
      {
        id: '25',
        title: 'Last Day of School for Students',
        start: `${currentYear}-06-26`,
        location: ''
      }
    ];

    // Filter events that are upcoming (in the future)
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set today to midnight for accurate date comparison

    const futureEvents = schoolEvents
      .map(event => {
        // Pre-process the date string for consistent Date object creation
        let processedDateString = event.start;
        // Check if event.start is a string and matches YYYY-MM-DD format
        if (typeof event.start === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(event.start)) {
            processedDateString = event.start.replace(/-/g, '/');
        } else if (typeof event.start === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(event.start)) {
            // For YYYY-MM-DDTHH:MM:SS (no Z), it's typically parsed as local, so direct use is often fine.
            // Or, to be explicit, one could also do .replace('T', ' ').replace(/-/g, '/')
            // but often new Date() handles this form as local.
            processedDateString = event.start;
        }
        return { ...event, processedDate: new Date(processedDateString) };
      })
      .filter(event => event.processedDate >= today)
      .sort((a, b) => a.processedDate - b.processedDate)
      .slice(0, 3); // Get only 3 most upcoming events
    
    // Format events for the homepage
    const formattedEvents = futureEvents.map(event => {
      const eventDate = event.processedDate; // Use the pre-processed and correctly parsed Date object
      
      // Extract time if it's not an all-day event (if time is specified in original string)
      const hasTime = event.start.includes('T');
      const timeString = hasTime ? 
        eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 
        'All Day';
      
      // Format date parts using the correctly parsed eventDate
      const month = eventDate.toLocaleDateString('en-US', { month: 'long' });
      const day = eventDate.getDate();
        
      return {
        id: event.id,
        title: event.title,
        date: `${month} ${day}`,
        time: timeString,
        location: event.location
      };
    });
    
    setUpcomingEvents(formattedEvents);
  }, []);
  
  // Extended student programs data with more programs
  // const studentPrograms = [
  //   {
  //     id: 1,
  //     title: 'Career Technical Education',
  //     image: '/images/homepage/PS52-program1.jpg'
  //   },
  //   {
  //     id: 2,
  //     title: 'Early Childhood Education',
  //     image: '/images/homepage/PS52-program2.jpg'
  //   },
  //   {
  //     id: 3,
  //     title: 'English Learner Programs',
  //     image: '/images/homepage/PS52-program3.jpg'
  //   },
  //   {
  //     id: 4,
  //     title: 'Award-Winning Music Programs',
  //     image: '/images/homepage/PS52-program4.jpg'
  //   },
  //   {
  //     id: 5,
  //     title: 'STEM Education Initiative',
  //     image: '/images/homepage/PS52-program5.jpg'
  //   },
  //   {
  //     id: 6,
  //     title: 'After-School Enrichment',
  //     image: '/images/homepage/PS52-program6.jpg'
  //   },
  //   {
  //     id: 7,
  //     title: 'Special Education Services',
  //     image: '/images/homepage/PS52-program7.jpg'
  //   },
  //   {
  //     id: 8,
  //     title: 'Visual & Performing Arts',
  //     image: '/images/homepage/PS52-program8.jpg'
  //   }
  // ];

  // Update carousel logic for Sanity data
  const handlePreviousSanity = () => {
    const programCount = homePageData?.programItems?.filter(Boolean).length || 0;
    if (programCount === 0) return;
    setProgramIndex((prevIndex) => 
      prevIndex === 0 ? programCount - 1 : prevIndex - 1
    );
  };

  const handleNextSanity = () => {
    const programCount = homePageData?.programItems?.filter(Boolean).length || 0;
    if (programCount === 0) return;
    setProgramIndex((prevIndex) => 
      prevIndex === programCount - 1 ? 0 : prevIndex + 1
    );
  };

  // Update getVisiblePrograms for Sanity data (showing 4)
  const getVisibleProgramsSanity = () => {
    if (!homePageData?.programItems || homePageData.programItems.length === 0) {
      return [];
    }
    const programs = homePageData.programItems.filter(Boolean);
    const programCount = programs.length;

    if (programCount === 0) {
      return [];
    }

    const visibleItems = [];
    for (let i = 0; i < 4; i++) {
        // Ensure index wraps around correctly if fewer than 4 programs exist
        const index = (programIndex + i) % programCount; 
        visibleItems.push(programs[index]);
    }
    return visibleItems;
  };

  // News Carousel Logic
  const handlePreviousNews = () => {
    const newsCount = homePageData?.newsItems?.length || 0;
    if (newsCount <= 3) return; // No rotation if 3 or fewer items
    setNewsIndex((prevIndex) =>
      prevIndex === 0 ? newsCount - 1 : prevIndex - 1
    );
  };

  const handleNextNews = () => {
    const newsCount = homePageData?.newsItems?.length || 0;
    if (newsCount <= 3) return; // No rotation if 3 or fewer items
    setNewsIndex((prevIndex) =>
      (prevIndex + 1) % newsCount
    );
  };

  const getVisibleNewsItems = () => {
    if (!homePageData?.newsItems || homePageData.newsItems.length === 0) {
      return [];
    }
    const allNews = homePageData.newsItems;
    const newsCount = allNews.length;

    if (newsCount <= 3) {
      return allNews; // Show all if 3 or fewer
    }

    const visibleItems = [];
    for (let i = 0; i < 3; i++) {
      const currentIndex = (newsIndex + i) % newsCount;
      visibleItems.push(allNews[currentIndex]);
    }
    return visibleItems;
  };

  const visibleNewsItems = getVisibleNewsItems();

  // --- Render Logic ---
  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>; // Basic loading state centered
  }

  if (error) {
    return <div className="flex justify-center items-center h-screen">Error: {error}</div>; // Basic error state centered
  }

  if (!homePageData) {
    return <div className="flex justify-center items-center h-screen">No page data found.</div>; // Handle missing data
  }

  const visibleProgramsSanity = getVisibleProgramsSanity(); // Calculate visible programs based on Sanity data
  const welcomeVideoUrl = homePageData.videoFile?.asset?.url;
  const welcomeImageUrl = homePageData.welcomeImage?.asset?.url;

  return (
    <div className="bg-white">
      {/* Banner Video Section */}
      <section className="relative">
        <div className="relative h-[100vh] max-h-[95vh] overflow-hidden">
          {/* Background Video */}
          {welcomeVideoUrl ? (
            <video 
              autoPlay 
              loop 
              muted 
              playsInline 
              poster={welcomeImageUrl} // Use image as poster
              className="absolute inset-0 w-full h-full object-fill object-center z-0"
            >
              <source src={welcomeVideoUrl} type="video/mp4" /> {/* Use Sanity Video URL */}
              Your browser does not support the video tag.
            </video>
          ) : welcomeImageUrl ? (
             <img 
               src={welcomeImageUrl} // Use Sanity Image URL
               alt={homePageData.welcomeHeading || "Welcome banner"}
               className="absolute inset-0 w-full h-full object-fill object-center z-0" 
             />
           ) : (
            <div className="absolute inset-0 w-full h-full object-cover object-center z-0 bg-gray-300"></div> // Placeholder background
           )}
          
          {/* Gradient overlay for better text visibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-black/10 to-black/5 z-10"></div>
          
          {/* Content overlay - positioned at bottom left */}
          <div className="absolute inset-0 flex items-end z-20">
            <div className="w-full px-6 md:px-12 lg:px-16">
              {/* Using static content from the first slide for the overlay */}
              <div className="max-w-xl pb-16 md:pb-16 lg:pb-16 text-left">
                {/* Horizontal line above title */}
                <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
                
                <h2 className="text-white text-4xl md:text-5xl font-bold mb-4 leading-tight text-left">
                  {homePageData.welcomeHeading} {/* Use Sanity Heading */}
                </h2>
                
                <p className="text-white text-lg mb-8 max-w-lg font-light text-left">
                  {homePageData.welcomeText} {/* Use Sanity Text */}
                </p>
                
                <Link 
                  to="/learn-more"
                  className="inline-flex items-center bg-white text-gray-900 hover:bg-gray-100 font-semibold px-8 py-3 transition-colors group"
                >
                  {homePageData.welcomeButtonText} {/* Use Sanity Button Text */}
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-4 w-4 ml-2 transform transition-transform group-hover:translate-x-1" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section - Improved with interactions */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 md:gap-4">
            <Link to="/safety" className="flex flex-col items-center w-32 text-center no-underline group">
              <div className="w-16 h-16 rounded-full border border-[#691e31] flex items-center justify-center mb-3 group-hover:bg-[#691e31] group-hover:text-white text-[#691e31] transition-all duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-gray-700 text-xs font-medium uppercase m-0 group-hover:text-[#691e31] transition-colors">STUDENT SAFETY</h3>
            </Link>
            <a href="https://www.schools.nyc.gov/school-life/food/menus" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center w-32 text-center no-underline group">
              <div className="w-16 h-16 rounded-full border border-[#691e31] flex items-center justify-center mb-3 group-hover:bg-[#691e31] group-hover:text-white text-[#691e31] transition-all duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-gray-700 text-xs font-medium uppercase m-0 group-hover:text-[#691e31] transition-colors">SCHOOL MEALS</h3>
            </a>
            <Link to="/calendar" className="flex flex-col items-center w-32 text-center no-underline group">
              <div className="w-16 h-16 rounded-full border border-[#691e31] flex items-center justify-center mb-3 group-hover:bg-[#691e31] group-hover:text-white text-[#691e31] transition-all duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-gray-700 text-xs font-medium uppercase m-0 group-hover:text-[#691e31] transition-colors">CALENDARS</h3>
            </Link>
            <a href="https://www.classdojo.com/" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center w-32 text-center no-underline group">
              <div className="w-16 h-16 rounded-full border border-[#691e31] flex items-center justify-center mb-3 group-hover:bg-[#691e31] group-hover:text-white text-[#691e31] transition-all duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
              </div>
              <h3 className="text-gray-700 text-xs font-medium uppercase m-0 group-hover:text-[#691e31] transition-colors">CLASSDOJO</h3>
            </a>
            <a href="https://schoolsaccount.nyc" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center w-32 text-center no-underline group">
              <div className="w-16 h-16 rounded-full border border-[#691e31] flex items-center justify-center mb-3 group-hover:bg-[#691e31] group-hover:text-white text-[#691e31] transition-all duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-gray-700 text-xs font-medium uppercase m-0 group-hover:text-[#691e31] transition-colors">NYC SCHOOLS ACCOUNT</h3>
            </a>
            <a href="https://app.operoo.com/" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center w-32 text-center no-underline group">
              <div className="w-16 h-16 rounded-full border border-[#691e31] flex items-center justify-center mb-3 group-hover:bg-[#691e31] group-hover:text-white text-[#691e31] transition-all duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-gray-700 text-xs font-medium uppercase m-0 group-hover:text-[#691e31] transition-colors">OPEROO</h3>
            </a>
          </div>
        </div>
      </section>

      {/* Featured News Section - Improved styling */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-[#2a4865] text-3xl font-bold relative after:content-[''] after:absolute after:w-16 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-0">
              {homePageData.newsHeading} {/* Use Sanity Heading */}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Use _id as key, access dereferenced fields */}
            {visibleNewsItems.map(news => (
              <div key={news._id} className="flex flex-col group">
                {news.slug?.current ? (
                  <Link to={`/news/${news.slug.current}`} className="block h-64 bg-gray-200 relative overflow-hidden">
                    {news.mainImage?.asset?.url && (
                      <img
                        src={news.mainImage.asset.url}
                        alt={news.title}
                        className="absolute inset-0 w-full h-full object-fill transition-transform duration-500 group-hover:scale-105"
                      />
                    )}
                  </Link>
                ) : (
                  <div className="h-64 bg-gray-200 relative overflow-hidden">
                    {news.mainImage?.asset?.url && (
                      <img
                        src={news.mainImage.asset.url}
                        alt={news.title}
                        className="absolute inset-0 w-full h-full object-fill transition-transform duration-500 group-hover:scale-105"
                      />
                    )}
                  </div>
                )}
                <div className="pt-4">
                  {/* Use news.publicationDate */}
                  <span className="text-[#691e31] text-sm font-medium">{formatDate(news.publicationDate)}</span>
                  <h3 className="text-[#2a4865] text-xl font-bold mt-2 mb-2 group-hover:text-[#691e31] transition-colors">
                    {news.title} {/* Use Sanity Title */}
                  </h3>
                  <p className="text-gray-600 mb-3">
                    {news.excerpt}
                  </p>
                  {/* Use news.slug */}
                  {news.slug?.current && (
                    <Link
                      to={`/news/${news.slug.current}`}
                      className="text-[#691e31] hover:underline font-medium inline-flex items-center cursor-pointer"
                    >
                      Read More
                      {/* SVG icon */}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
          {homePageData?.newsItems?.length > 3 && (
            <div className="flex justify-center gap-3 mt-8">
              <button
                className="w-10 h-10 bg-[#2a4865]/80 hover:bg-[#2a4865] rounded-full flex items-center justify-center text-white cursor-pointer transition-all duration-300"
                onClick={handlePreviousNews}
                aria-label="Previous News Article"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                className="w-10 h-10 bg-[#2a4865]/80 hover:bg-[#2a4865] rounded-full flex items-center justify-center text-white cursor-pointer transition-all duration-300"
                onClick={handleNextNews}
                aria-label="Next News Article"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </section>
      
      {/* Student Programs Section - Improved styling */}
      <section className="py-16 bg-[#2a4865] text-white">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-10">
            <h2 className="text-white text-3xl font-bold relative after:content-[''] after:absolute after:w-16 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-0">
              {homePageData.programsHeading} {/* Use Sanity Heading */}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {visibleProgramsSanity.map(program => (
              <Link to={`/programs/${program.slug}`} key={program._key} className="block relative group overflow-hidden no-underline">
                <div className="h-60 bg-gray-700 overflow-hidden">
                  {program.image?.asset?.url && (
                    <img 
                      src={program.image.asset.url} 
                      alt={program.title}
                      className="absolute inset-0 w-full h-full object-fill transition-transform duration-500 group-hover:scale-110"
                    />
                  )}
                </div>
                <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/80 to-transparent p-4">
                  <div className="flex items-center">
                    <div className="w-1 h-12 bg-yellow-500 mr-3"></div>
                    <h3 className="text-white text-lg font-medium">
                      {program.title} {/* Use Sanity Title */}
                    </h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="flex justify-center gap-3 mt-8">
            <button 
              className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white cursor-pointer transition-all duration-300"
              onClick={handlePreviousSanity}
              aria-label="Previous Program"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button 
              className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white cursor-pointer transition-all duration-300"
              onClick={handleNextSanity}
              aria-label="Next Program"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* School Statistics Section - Use Sanity Data */}
      <section className="py-16 border-t border-gray-100">
        <div className="container mx-auto px-4">
          <h2 className="text-[#2a4865] text-3xl font-bold text-center mb-16 relative after:content-[''] after:absolute after:w-24 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-1/2 after:-translate-x-1/2">
            {homePageData.statisticsHeading} {/* Use Sanity Heading */}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-14">
            {/* Map over statisticsItems */} 
            {homePageData.statisticsItems?.map((item, index) => (
                <div key={item._key} className="flex flex-col items-center text-center">
                  {/* Keep icon structure, use index to alternate */}
                  <div className="text-[#691e31] mb-5">
                    {index === 0 && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-14 h-14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    )}
                    {index === 1 && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-14 h-14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    )}
                    {index === 2 && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-14 h-14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                       </svg>
                    )}
                    {/* Add more conditions if more stats are expected */} 
                  </div>
                  <span className="text-[#2a4865] text-5xl font-bold">{item.value}</span> {/* Use Sanity value */}
                  <span className="text-gray-600 mt-2">{item.label}</span> {/* Use Sanity label */}
                  <div className="w-16 h-1 bg-gray-200 mt-6"></div>
                </div>
            ))}
          </div>

          {/* Optional single stat at the bottom */} 
          {(homePageData.statisticsFootnoteValue && homePageData.statisticsFootnoteLabel) && (
             <div className="flex justify-center mt-16">
               <div className="text-center max-w-4xl">
                 <div className="text-[#691e31] mb-5">
                   {/* Keep original icon */}
                   <svg xmlns="http://www.w3.org/2000/svg" className="w-20 h-20 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                   </svg>
                 </div>
                 <span className="text-[#2a4865] text-6xl font-bold">{homePageData.statisticsFootnoteValue}</span> {/* Use Sanity value */}
                 <span className="text-gray-600 block mt-2">{homePageData.statisticsFootnoteLabel}</span> {/* Use Sanity label */}
               </div>
             </div>
          )}
        </div>
      </section>

      {/* Upcoming Events Section - Use Sanity Heading, dynamic events + Sanity Mission */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-1/3">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-[#2a4865] text-2xl font-bold relative after:content-[''] after:absolute after:w-12 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-0">
                  {homePageData.eventsHeading}
                </h2>
                <Link to="/calendar" className="text-[#691e31] hover:underline font-medium text-sm flex items-center gap-1 group">
                  All Events
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
              <div className="flex flex-col space-y-4 mt-8">
                {upcomingEvents.map(event => (
                  <div key={event.id} className="flex border-b border-gray-200 pb-4 last:border-0 group hover:bg-white hover:shadow-sm transition-all duration-300 p-2 rounded">
                    <div className="flex flex-col items-center justify-center bg-[#f8f9fa] text-[#2a4865] p-4 mr-4 min-w-[80px] text-center group-hover:bg-[#691e31]/10">
                      <span className="uppercase text-xs font-medium text-[#691e31]">{event.date.split(' ')[0]}</span>
                      <span className="text-2xl font-bold">{event.date.split(' ')[1].replace(',', '')}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-[#2a4865] mb-1 group-hover:text-[#691e31] transition-colors">{event.title}</h3>
                      <p className="text-gray-600 text-sm">{event.time}</p>
                      {event.location && <p className="text-gray-600 text-sm">{event.location}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Mission Section - Use Sanity Data */}
            <div className="lg:w-2/3 bg-white p-8 rounded shadow-sm">
              <h3 className="text-[#2a4865] text-2xl font-bold mb-6 relative after:content-[''] after:absolute after:w-12 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-0">
                {homePageData.missionTitle} {/* Use Sanity Title */}
              </h3>
              <div className="mt-8">
                {/* Render missionText - assumes basic paragraph structure, split by newline */}
                {homePageData.missionText?.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="text-gray-700 mb-4 last:mb-0 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;